<script setup lang="ts">
/**
 * Table cell for a non-negative integer (visit count, number of clients, etc.).
 *
 * Validates that `value` is a finite integer ≥ 0 before rendering. Any other
 * input (negative, float, `NaN`, `Infinity`, `null`, `undefined`) shows
 * `emptyPlaceholder` in muted style. Valid values are formatted with locale-
 * aware digit grouping via `toLocaleString`. Center-aligned to match other
 * numeric cells.
 */
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    /** Integer value to display. Rendered only when it is a finite, non-negative integer. */
    value?: number | null
    /** Placeholder shown for missing, non-finite, non-integer, or negative values. */
    emptyPlaceholder?: string
    /** Locale for digit grouping (e.g. "en-US"); defaults to the runtime locale. */
    locale?: string
    /** Max width of the cell (CSS value). Set by DynamicTable from column config. */
    maxWidth?: string
  }>(),
  { emptyPlaceholder: '–', locale: undefined },
)

const isValid = computed(
  () =>
    typeof props.value === 'number' &&
    Number.isFinite(props.value) &&
    Number.isInteger(props.value) &&
    props.value >= 0,
)

const formatted = computed(() =>
  isValid.value
    ? (props.value as number).toLocaleString(props.locale, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      })
    : null,
)
</script>

<template>
  <!--
    Numeric values are center-aligned across the app for visual consistency
    with other numeric cells (`MoneyCell`, `NumberCell`, `RowIndexCell`).
    Column headers should set `align: 'center'` in their config so the `<th>`
    label sits over the centered values.
  -->
  <td
    class="font-tufte text-tufte-text positive-integer-cell"
    :style="props.maxWidth ? { maxWidth: props.maxWidth, overflowWrap: 'break-word' } : undefined"
  >
    <span v-if="formatted !== null">{{ formatted }}</span>
    <span v-else class="text-tufte-muted">{{ props.emptyPlaceholder }}</span>
  </td>
</template>

<style scoped>
.positive-integer-cell {
  text-align: center;
}
</style>
